import { cn } from "@/lib/utils";
import { X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const FilterPill = ({ 
  label, 
  value,
  onRemove,
  className 
}) => {
  return (
    <span 
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
        "bg-electric-ice-blue/10 text-electric-ice-blue text-sm",
        className
      )}
      data-testid="filter-pill"
    >
      <span className="text-soft-silver">{label}:</span>
      <span className="font-medium">{value}</span>
      {onRemove && (
        <button 
          onClick={onRemove}
          className="ml-1 hover:bg-white/[0.1] rounded p-0.5"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};

export const FilterDropdown = ({ 
  label, 
  options = [],
  value,
  onChange,
  className 
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "border-white/[0.06] hover:bg-white/[0.05] text-soft-silver",
            "h-9 px-3 gap-2",
            value && "text-cloud-white border-electric-ice-blue/30",
            className
          )}
          data-testid="filter-dropdown"
        >
          {label}{value && `: ${value}`}
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="bg-graphite border-white/[0.06] text-cloud-white min-w-[150px]"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange?.(option.value)}
            className={cn(
              "cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]",
              value === option.value && "bg-electric-ice-blue/10 text-electric-ice-blue"
            )}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const FilterBar = ({ 
  filters = [],
  activeFilters = [],
  onFilterChange,
  onClearAll,
  className 
}) => {
  return (
    <div 
      className={cn("flex flex-wrap items-center gap-3", className)}
      data-testid="filter-bar"
    >
      {/* Filter Dropdowns */}
      {filters.map((filter) => (
        <FilterDropdown
          key={filter.key}
          label={filter.label}
          options={filter.options}
          value={activeFilters.find(f => f.key === filter.key)?.value}
          onChange={(value) => onFilterChange?.(filter.key, value)}
        />
      ))}
      
      {/* Active Filter Pills */}
      {activeFilters.length > 0 && (
        <>
          <div className="h-6 w-px bg-white/[0.08] mx-2" />
          {activeFilters.map((filter) => (
            <FilterPill
              key={filter.key}
              label={filter.label}
              value={filter.value}
              onRemove={() => onFilterChange?.(filter.key, null)}
            />
          ))}
          <button
            onClick={onClearAll}
            className="text-sm text-muted-text hover:text-soft-silver"
            data-testid="clear-filters"
          >
            Clear all
          </button>
        </>
      )}
    </div>
  );
};

export default FilterBar;
